<template>
    <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
        <div class="refund-modal">
            <div class="modal-header">
                <h2>Remboursement de la vente</h2>
                <button class="close-btn" @click="$emit('close')">×</button>
            </div>

            <div class="modal-body">
                <div v-if="errorMessage">
                    <div class="alert alert-danger">
                        {{ errorMessage }}
                    </div>
                </div>
                <!-- Type de remboursement -->
                <div class="refund-type">
                    <label class="radio-option" :class="{ active: !isPartial }">
                        <input type="radio" :value="false" v-model="isPartial">
                        <div class="option-content">
                            <span class="option-title">Remboursement total</span>
                            <span class="option-description">Rembourser tous les articles</span>
                        </div>
                    </label>
                    <label class="radio-option" :class="{ active: isPartial }">
                        <input type="radio" :value="true" v-model="isPartial">
                        <div class="option-content">
                            <span class="option-title">Remboursement partiel</span>
                            <span class="option-description">Rembourser certains articles</span>
                        </div>
                    </label>
                </div>

                <!-- Liste des articles (pour remboursement partiel) -->
                <div v-if="isPartial" class="items-section">
                    <h3>Sélectionnez les articles à rembourser</h3>
                    <div class="items-list">
                        <div v-for="item in saleItems" :key="item.id" class="item-row">
                            <div class="item-info">
                                <div class="item-image">
                                    <img v-if="item.product.image_url" :src="item.product.image_url"
                                        :alt="item.product.name">
                                    <div v-else class="no-image">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                            <polyline points="21 15 16 10 5 21"></polyline>
                                        </svg>
                                    </div>
                                </div>
                                <div class="item-details">
                                    <p class="item-name">{{ item.product.name }}</p>
                                    <p class="item-price">{{ formatCurrency(item.unit_price) }} × {{ item.quantity }}
                                    </p>
                                </div>
                            </div>
                            <div class="item-quantity">
                                <label>Quantité à rembourser</label>
                                <div class="quantity-input">
                                    <button @click="decrementQuantity(item.id)"
                                        :disabled="getRefundQuantity(item.id) === 0">-</button>
                                    <input type="number" :value="getRefundQuantity(item.id)"
                                        @input="updateQuantity(item.id, $event)" :max="item.quantity" min="0">
                                    <button @click="incrementQuantity(item.id)"
                                        :disabled="getRefundQuantity(item.id) >= item.quantity">+
                                    </button>
                                </div>
                                <span class="max-qty">Max: {{ item.quantity }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Récapitulatif -->
                <div class="summary-section">
                    <h3>Récapitulatif</h3>
                    <div class="summary-content">
                        <div class="summary-row">
                            <span>Montant total de la vente</span>
                            <span>{{ formatCurrency(totalAmount) }}</span>
                        </div>
                        <div class="summary-row highlight">
                            <span>Montant à rembourser</span>
                            <span class="refund-amount">{{ formatCurrency(refundAmount) }}</span>
                        </div>
                        <div v-if="isPartial" class="summary-row">
                            <span>Articles à rembourser</span>
                            <span>{{ selectedItemsCount }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <button @click="$emit('close')" class="btn-secondary" :disabled="isProcessing">
                    Annuler
                </button>
                <button @click="processRefund" class="btn-danger" :disabled="!canProcess || isProcessing">
                    <span v-if="isProcessing" class="spinner"></span>
                    {{ isProcessing ? 'Traitement...' : 'Confirmer le remboursement' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { salesService } from '@/services';
import CashRegisterService from '@/services/cashRegisterService';
import { useToastStore } from '@/store/modules/toastStore';
export default {
    name: 'RefundModal',
    props: {
        show: {
            type: Boolean,
            required: true
        },
        sale: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'refunded'],
    setup(props, { emit }) {
        const toastStore = useToastStore();
        const errorMessage = ref('');
        const isPartial = ref(false);
        const refundItems = ref([]);
        const cashRegisterId = ref('');
        const isProcessing = ref(false);

        const saleItems = computed(() => props.sale.sale_items || []);
        const totalAmount = computed(() => parseFloat(props.sale.total_incl_tax || 0));

        const refundAmount = computed(() => {
            if (!isPartial.value) {
                return totalAmount.value;
            }

            let total = 0;
            refundItems.value.forEach(refundItem => {
                const saleItem = saleItems.value.find(item => item.id === refundItem.sale_item_id);
                if (saleItem && refundItem.quantity > 0) {
                    const refundRatio = refundItem.quantity / saleItem.quantity;
                    total += saleItem.total_amount * refundRatio;
                }
            });
            return total;
        });

        const selectedItemsCount = computed(() => {
            return refundItems.value.filter(item => item.quantity > 0).length;
        });

        const canProcess = computed(() => {
            if (isPartial.value) {
                return selectedItemsCount.value > 0 && refundAmount.value > 0;
            }
            return true;
        });

        const initializeRefundItems = () => {
            refundItems.value = saleItems.value.map(item => ({
                sale_item_id: item.id,
                quantity: 0
            }));
        };

        const getRefundQuantity = (itemId) => {
            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            return refundItem ? refundItem.quantity : 0;
        };

        const updateQuantity = (itemId, event) => {
            const value = parseInt(event.target.value) || 0;
            const saleItem = saleItems.value.find(item => item.id === itemId);
            const maxQuantity = saleItem ? saleItem.quantity : 0;

            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            if (refundItem) {
                refundItem.quantity = Math.max(0, Math.min(value, maxQuantity));
            }
        };

        const incrementQuantity = (itemId) => {
            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            const saleItem = saleItems.value.find(item => item.id === itemId);
            if (refundItem && saleItem && refundItem.quantity < saleItem.quantity) {
                refundItem.quantity++;
            }
        };

        const decrementQuantity = (itemId) => {
            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            if (refundItem && refundItem.quantity > 0) {
                refundItem.quantity--;
            }
        };

        const processRefund = async () => {
            if (!canProcess.value) return;

            try {
                const cashRegister = await CashRegisterService.getMyOpenedCashRegister();
                console.log('cashRegister', cashRegister);
                cashRegisterId.value = cashRegister.data.id;

            } catch (error) {
                errorMessage.value = error.message;
                return;
            }

            try {
                isProcessing.value = true;

                

                const refundData = {
                    partial: isPartial.value,
                    cash_register_id: cashRegisterId.value
                };

                if (isPartial.value) {
                    refundData.items = refundItems.value.filter(item => item.quantity > 0);
                }

                console.log('TODO: Envoyer les données de remboursement', refundData);

                const response = await salesService.refundSale(props.sale.id, refundData);
                console.clear();
                console.log('response', response.data);

                emit('refunded');
                emit('close');
            } catch (error) {
                console.clear();
                console.error('Erreur remboursement:', error);

                const errorMessage = error.response.data.message || 'Erreur remboursement';
                toastStore.addToast({
                    type: 'error',
                    message: errorMessage,
                });
                // TODO: Afficher une notification d'erreur
            } finally {
                isProcessing.value = false;
            }
        };

        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount || 0);
        };

        const formatDate = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        };

        watch(() => props.show, (newVal) => {
            if (newVal) {
                initializeRefundItems();
                isPartial.value = false;
                cashRegisterId.value = '';
            }
        });

        watch(isPartial, () => {
            initializeRefundItems();
        });

        return {
            isPartial,
            refundItems,
            cashRegisterId,
            isProcessing,
            saleItems,
            totalAmount,
            refundAmount,
            selectedItemsCount,
            canProcess,
            errorMessage,
            getRefundQuantity,
            updateQuantity,
            incrementQuantity,
            decrementQuantity,
            processRefund,
            formatCurrency,
            formatDate
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.refund-modal {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s;
}

.close-btn:hover {
    background: #f3f4f6;
}

.modal-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
}

.refund-type {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.radio-option {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
}

.radio-option:hover {
    border-color: #cbd5e1;
}

.radio-option.active {
    border-color: #0A5C6B;
    background: #f0fdfa;
}

.radio-option input {
    margin-top: 0.25rem;
}

.option-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.option-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
}

.option-description {
    font-size: 0.75rem;
    color: #6b7280;
}

.items-section {
    margin-bottom: 1.5rem;
}

.items-section h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1rem 0;
}

.items-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    gap: 1rem;
}

.item-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
}

.item-image {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    overflow: hidden;
    background: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.item-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.no-image {
    color: #9ca3af;
}

.item-details {
    flex: 1;
}

.item-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: #111827;
    margin: 0 0 0.25rem 0;
}

.item-price {
    font-size: 0.8rem;
    color: #6b7280;
    margin: 0;
}

.item-quantity {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
}

.item-quantity label {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.25rem;
}

.quantity-input {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.quantity-input button {
    width: 28px;
    height: 28px;
    border: 1px solid #e5e7eb;
    background: white;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6b7280;
    font-weight: 600;
    transition: all 0.2s;
}

.quantity-input button:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
}

.quantity-input button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.quantity-input input {
    width: 50px;
    height: 28px;
    text-align: center;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    font-size: 0.85rem;
}

.max-qty {
    font-size: 0.7rem;
    color: #9ca3af;
}

.summary-section {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
}

.summary-section h3 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.75rem 0;
}

.summary-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    color: #6b7280;
}

.summary-row.highlight {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    padding-top: 0.5rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 0.25rem;
}

.refund-amount {
    color: #dc2626;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
}

.required {
    color: #dc2626;
}

.form-group select {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.9rem;
    background: white;
}

.form-group select:focus {
    outline: none;
    border-color: #0A5C6B;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
}

.btn-secondary,
.btn-danger {
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #e5e7eb;
}

.btn-secondary:hover:not(:disabled) {
    background: #f9fafb;
}

.btn-danger {
    background: #dc2626;
    color: white;
}

.btn-danger:hover:not(:disabled) {
    background: #b91c1c;
}

.btn-secondary:disabled,
.btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .refund-type {
        grid-template-columns: 1fr;
    }

    .item-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .item-quantity {
        align-self: flex-end;
    }
}
</style>